import React, { useState } from 'react';
import { 
  X, 
  User, 
  MapPin, 
  FileText, 
  Calculator, 
  Key, 
  CheckCircle,
  Building,
  CreditCard,
  Shield,
  Briefcase,
  Camera,
  Mail,
  Phone
} from 'lucide-react';
import { updateDoc, doc, Timestamp } from 'firebase/firestore';

const EditEmployeeModal = ({ employee, db, roles, onClose }) => {
  // Initialiser l'adresse comme un objet avec des propriétés par défaut
  const initialAddress = employee?.address ? (
    typeof employee.address === 'string' 
      ? { street: employee.address, city: '', postalCode: '', country: 'France' }
      : { 
          street: employee.address.street || '',
          city: employee.address.city || '',
          postalCode: employee.address.postalCode || '',
          country: employee.address.country || 'France'
        }
  ) : { street: '', city: '', postalCode: '', country: 'France' };

  // Initialiser les informations professionnelles
  const initialProfessionalInfo = employee?.professionalInfo || {
    contractType: '',
    startDate: '',
    endDate: '',
    weeklyHours: '',
    cesuNumber: '',
    nanCrcesu: ''
  };

  // Initialiser les informations bancaires
  const initialBankInfo = employee?.bankInfo || {
    iban: '',
    bic: ''
  };

  // Initialiser les tarifs de déplacement
  const initialDeplacementRate = employee?.deplacementRate || {
    base: 2.50,
    includedDistance: 20,
    perKm: 0.40
  };

  const [formData, setFormData] = useState({
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    phoneNumber: employee?.phoneNumber || '',
    birthDate: employee?.birthDate ? (
      employee.birthDate.toDate ? 
        employee.birthDate.toDate().toISOString().split('T')[0] : 
        new Date(employee.birthDate).toISOString().split('T')[0]
    ) : '',
    address: initialAddress,
    role: employee?.role || '',
    isActive: employee?.isActive !== false,
    hireDate: employee?.hireDate ? (
      employee.hireDate.toDate ? 
        employee.hireDate.toDate().toISOString().split('T')[0] : 
        new Date(employee.hireDate).toISOString().split('T')[0]
    ) : '',
    color: employee?.color || '#808080',
    isAutoEntrepreneur: employee?.isAutoEntrepreneur || false,
    hourlyRate: employee?.hourlyRate || '',
    socialSecurity: employee?.socialSecurity || '',
    bankInfo: initialBankInfo,
    professionalInfo: initialProfessionalInfo,
    defaultRadius: employee?.defaultRadius || 10,
    deplacementRate: initialDeplacementRate
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Préparer les données pour Firestore
      const employeeData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        role: formData.role,
        isActive: formData.isActive,
        hireDate: formData.hireDate ? Timestamp.fromDate(new Date(formData.hireDate)) : null,
        professionalInfo: formData.professionalInfo,
        bankInfo: formData.bankInfo,
        socialSecurity: formData.socialSecurity,
        hourlyRate: formData.hourlyRate,
        isAutoEntrepreneur: formData.isAutoEntrepreneur,
        deplacementRate: formData.deplacementRate,
        defaultRadius: formData.defaultRadius,
        color: formData.color,
        birthDate: formData.birthDate ? Timestamp.fromDate(new Date(formData.birthDate)) : null,
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(db, 'employees', employee.id), employeeData);
      alert('Intervenant modifié avec succès');
      onClose();
    } catch (error) {
      console.error('Erreur modification intervenant:', error);
      alert('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const predefinedColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', 
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#fa709a', '#fee140', '#a8edea', '#fed6e3'
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Modifier l'intervenant</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Mettez à jour les informations de {employee?.firstName} {employee?.lastName}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Informations principales */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <User size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations principales
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center mx-auto">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold text-xl">
                    {formData.firstName ? formData.firstName.charAt(0).toUpperCase() : 'I'}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mt-3">
                  {formData.firstName || 'Intervenant'} {formData.lastName || ''}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{formData.role}</p>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                  formData.isActive 
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                }`}>
                  {formData.isActive ? 'Actif' : 'Inactif'}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleChange('phoneNumber', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance</label>
                    <input
                      type="date"
                      value={formData.birthDate}
                      onChange={(e) => handleChange('birthDate', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date d'embauche</label>
                    <input
                      type="date"
                      value={formData.hireDate}
                      onChange={(e) => handleChange('hireDate', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <FileText size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations professionnelles
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de contrat</label>
                <select
                  value={formData.professionalInfo.contractType}
                  onChange={(e) => handleChange('professionalInfo', {...formData.professionalInfo, contractType: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un type</option>
                  <option value="Indépendant">Indépendant</option>
                  <option value="Cesu">Cesu</option>
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="Interim">Intérim</option>
                  <option value="Stage">Stage</option>
                </select>
              </div>
              {formData.professionalInfo.contractType === 'Cesu' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numéro CESU</label>
                    <input
                      type="text"
                      value={formData.professionalInfo.cesuNumber}
                      onChange={(e) => handleChange('professionalInfo', {...formData.professionalInfo, cesuNumber: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 123456789"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code NAN CRCESU</label>
                    <input
                      type="text"
                      value={formData.professionalInfo.nanCrcesu}
                      onChange={(e) => handleChange('professionalInfo', {...formData.professionalInfo, nanCrcesu: e.target.value})}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: SAP123456789"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone d'intervention (km)</label>
                <select
                  value={formData.defaultRadius}
                  onChange={(e) => handleChange('defaultRadius', parseInt(e.target.value))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={5}>5 km</option>
                  <option value={10}>10 km</option>
                  <option value={15}>15 km</option>
                  <option value={20}>20 km</option>
                  <option value={30}>30 km</option>
                  <option value={50}>50 km</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un rôle</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numéro de sécurité sociale</label>
                <input
                  type="text"
                  value={formData.socialSecurity}
                  onChange={(e) => handleChange('socialSecurity', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="X XX XX XX XXX XXX XX"
                  maxLength="15"
                />
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <MapPin size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Adresse
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rue</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Numéro et nom de rue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code postal</label>
                <input
                  type="text"
                  value={formData.address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="75000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ville</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Paris"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pays</label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="France"
                />
              </div>
            </div>
          </div>

          {/* Auto-entrepreneur */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Calculator size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Auto-entrepreneur
            </h4>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={formData.isAutoEntrepreneur}
                onChange={(e) => handleChange('isAutoEntrepreneur', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Auto-entrepreneur</span>
            </div>
            
            {formData.isAutoEntrepreneur && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taux horaire (€)</label>
                  <input
                    type="number"
                    value={formData.hourlyRate}
                    onChange={(e) => handleChange('hourlyRate', parseFloat(e.target.value))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Forfait base (€)</label>
                  <input
                    type="number"
                    value={formData.deplacementRate.base}
                    onChange={(e) => handleChange('deplacementRate', {...formData.deplacementRate, base: parseFloat(e.target.value)})}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Distance incluse (km)</label>
                  <input
                    type="number"
                    value={formData.deplacementRate.includedDistance}
                    onChange={(e) => handleChange('deplacementRate', {...formData.deplacementRate, includedDistance: parseInt(e.target.value)})}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tarif/km (€)</label>
                  <input
                    type="number"
                    value={formData.deplacementRate.perKm}
                    onChange={(e) => handleChange('deplacementRate', {...formData.deplacementRate, perKm: parseFloat(e.target.value)})}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Informations bancaires */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Key size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Informations bancaires
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IBAN</label>
                <input
                  type="text"
                  value={formData.bankInfo.iban}
                  onChange={(e) => handleChange('bankInfo', {...formData.bankInfo, iban: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BIC</label>
                <input
                  type="text"
                  value={formData.bankInfo.bic}
                  onChange={(e) => handleChange('bankInfo', {...formData.bankInfo, bic: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ABCDEFGH"
                />
              </div>
            </div>
          </div>

          {/* Statut */}
          <div className="mb-6">
            <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <CheckCircle size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
              Statut
            </h4>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Intervenant actif</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
